/**
 * Thin wrapper over amazon-cognito-identity-js for a PUBLIC SPA app client
 * (no client secret; SRP runs in-browser). The rest of the app never imports
 * vendor types directly: it talks to this module's plain shapes.
 *
 * Token contract: the backend (FastAPI verifier + API Gateway JWT authorizer)
 * accepts the Cognito **ID token** as the Bearer. The ID token carries `email`,
 * `sub` and `cognito:groups`; the access token does not carry `email`, so we
 * always send the ID token. This module returns/refreshes the ID token JWT.
 */
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

/** Claims we read from the decoded ID token payload. */
export interface IdTokenClaims {
  sub: string;
  email: string | null;
  name: string | null;
  groups: string[];
}

/** Raised when Cognito asks for a forced password change on first sign-in. */
export class NewPasswordRequiredError extends Error {
  readonly code = 'NEW_PASSWORD_REQUIRED';
  constructor(message = 'É necessário definir uma nova senha antes de entrar.') {
    super(message);
    this.name = 'NewPasswordRequiredError';
  }
}

/** Raised when Cognito asks for an MFA step this console cannot answer. */
export class MfaNotSupportedError extends Error {
  readonly code = 'MFA_NOT_SUPPORTED';
  constructor(
    message = 'Esta conta exige verificação em duas etapas, que ainda não é suportada aqui. ' +
      'Fale com um administrador.',
  ) {
    super(message);
    this.name = 'MfaNotSupportedError';
  }
}

/**
 * The MFA callbacks `authenticateUser` may invoke, all of them rejecting.
 *
 * The pool's `MfaConfiguration` is OPTIONAL, so nothing stops a person from
 * turning TOTP on for their own account — and the SDK then calls
 * `totpRequired` / `mfaRequired` / `mfaSetup` / `selectMFAType`. A callback
 * object that omits them does not fail: the SDK simply has nothing to call, the
 * promise never settles, and the sign-in button spins forever with no error
 * anywhere. Rejecting turns an invisible hang into a message, which is the
 * honest state of the feature until the challenge screens exist.
 */
function rejectMfaChallenges(reject: (reason: Error) => void) {
  const refuse = () => reject(new MfaNotSupportedError());
  return {
    totpRequired: refuse,
    mfaRequired: refuse,
    mfaSetup: refuse,
    selectMFAType: refuse,
  };
}

/** Refresh the ID token when it is within this window of expiry. */
const REFRESH_SKEW_SECONDS = 120;

/**
 * Password policy mirrored from the Cognito pool (infra/modules/cognito ·
 * password_policy: min length + lower/upper/number/symbol). Kept here so the
 * console can validate and hint client-side before Cognito rejects. Keep this
 * in sync with the pool's `password_minimum_length` if it ever changes.
 */
export const PASSWORD_MIN_LENGTH = 6;

/** One-line pt-BR summary of the pool password requirements (field hints). */
export const PASSWORD_HINT = `Mínimo de ${PASSWORD_MIN_LENGTH} caracteres, com letra maiúscula, minúscula, número e símbolo.`;

/**
 * Validates a candidate password against the pool policy. Returns a pt-BR
 * message for the first unmet requirement, or null when it satisfies all of
 * them. Purely client-side pre-flight — Cognito remains the source of truth.
 */
export function passwordPolicyError(password: string): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `A senha deve ter pelo menos ${PASSWORD_MIN_LENGTH} caracteres.`;
  }
  if (!/[a-z]/.test(password)) return 'A senha deve conter uma letra minúscula.';
  if (!/[A-Z]/.test(password)) return 'A senha deve conter uma letra maiúscula.';
  if (!/[0-9]/.test(password)) return 'A senha deve conter um número.';
  // Any non-alphanumeric character counts as a symbol (covers Cognito's set).
  if (!/[^A-Za-z0-9]/.test(password)) return 'A senha deve conter um símbolo.';
  return null;
}

let pool: CognitoUserPool | null = null;

function getUserPool(): CognitoUserPool {
  if (pool) return pool;
  const UserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const ClientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  if (!UserPoolId || !ClientId) {
    throw new Error(
      'Configuração do Cognito ausente: defina VITE_COGNITO_USER_POOL_ID e VITE_COGNITO_CLIENT_ID.',
    );
  }
  // Region is implied by the pool id (e.g. "us-east-1_xxxxx").
  pool = new CognitoUserPool({ UserPoolId, ClientId });
  return pool;
}

function buildUser(email: string): CognitoUser {
  return new CognitoUser({ Username: email, Pool: getUserPool() });
}

/**
 * Authenticates via USER_SRP_AUTH. Resolves with the session on success.
 * Surfaces the newPasswordRequired challenge as a typed error instead of
 * leaving the promise pending.
 */
export function signIn(email: string, password: string): Promise<CognitoUserSession> {
  const user = buildUser(email);
  const details = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise<CognitoUserSession>((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
      newPasswordRequired: () => reject(new NewPasswordRequiredError()),
      ...rejectMfaChallenges(reject),
    });
  });
}

/** Creates an unconfirmed account; Cognito sends the verification code by e-mail. */
export function signUp(name: string, email: string, password: string): Promise<void> {
  const attributes = [
    new CognitoUserAttribute({ Name: 'name', Value: name.trim() }),
    new CognitoUserAttribute({ Name: 'email', Value: email.trim() }),
  ];
  return new Promise<void>((resolve, reject) => {
    getUserPool().signUp(email.trim(), password, attributes, [], (err) => {
      if (err) reject(new Error(signUpMessage(err)));
      else resolve();
    });
  });
}

/** Confirms the e-mailed code; no session is created before this succeeds. */
export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    buildUser(email.trim()).confirmRegistration(code.trim(), true, (err) => {
      if (err) reject(new Error(confirmationMessage(err)));
      else resolve();
    });
  });
}

/** Sends a fresh confirmation code to the same unconfirmed account. */
export function resendSignUpCode(email: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    buildUser(email.trim()).resendConfirmationCode((err) => {
      if (err) reject(new Error(confirmationMessage(err)));
      else resolve();
    });
  });
}

function signUpMessage(err: unknown): string {
  switch (errorKey(err)) {
    case 'InvalidPasswordException':
    case 'InvalidParameterException':
      return `Revise os dados informados. ${PASSWORD_HINT}`;
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
    case 'NetworkError':
      return 'Não foi possível alcançar o servidor de autenticação. Verifique sua conexão.';
    default:
      return 'Não foi possível criar a conta. Se você já se cadastrou, entre ou recupere sua senha.';
  }
}

function confirmationMessage(err: unknown): string {
  switch (errorKey(err)) {
    case 'CodeMismatchException':
    case 'UserNotFoundException':
      return 'Código inválido. Confira o código enviado por e-mail.';
    case 'ExpiredCodeException':
      return 'O código expirou. Solicite um novo código.';
    case 'NotAuthorizedException':
      return 'Esta conta já foi confirmada. Volte ao login para entrar.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
    case 'NetworkError':
      return 'Não foi possível alcançar o servidor de autenticação. Verifique sua conexão.';
    default:
      return 'Não foi possível confirmar o e-mail. Tente novamente.';
  }
}

/**
 * Answers the forced first password change with a password of the person's own.
 *
 * Every invited account arrives in `FORCE_CHANGE_PASSWORD`: AdminCreateUser mails
 * a temporary password and Cognito refuses to issue tokens until it is replaced.
 * Without this call the invitation is undeliverable in practice — the person
 * signs in correctly and is told to contact an administrator.
 *
 * SRP is run again from scratch rather than reusing the `CognitoUser` that
 * raised the challenge: the challenge session lives on that instance and cannot
 * be handed across a React render, so the temporary password is exchanged once
 * more here and the challenge is answered inside the same callback.
 *
 * `{}` as the required-attribute payload is deliberate: the pool's only required
 * attribute is `email`, which AdminCreateUser already set, and echoing the
 * attribute bag back would try to rewrite read-only claims (`email_verified`)
 * that Cognito rejects.
 */
export function completeNewPassword(
  email: string,
  temporaryPassword: string,
  newPassword: string,
): Promise<CognitoUserSession> {
  const user = buildUser(email);
  const details = new AuthenticationDetails({ Username: email, Password: temporaryPassword });

  return new Promise<CognitoUserSession>((resolve, reject) => {
    user.authenticateUser(details, {
      // The password was already changed (a second tab, a retry): the session is
      // the outcome the caller wanted, so this is success, not a surprise.
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
      newPasswordRequired: () => {
        user.completeNewPasswordChallenge(
          newPassword,
          {},
          {
            onSuccess: (session) => resolve(session),
            onFailure: (err) => reject(err),
            ...rejectMfaChallenges(reject),
          },
        );
      },
      ...rejectMfaChallenges(reject),
    });
  });
}

/**
 * Returns a valid ID token JWT for the current user, refreshing it when it is
 * within ~2 min of expiry. Returns null when there is no restorable session.
 */
export function getValidIdToken(): Promise<string | null> {
  const user = getUserPool().getCurrentUser();
  if (!user) return Promise.resolve(null);

  return new Promise<string | null>((resolve) => {
    user.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error || !session || !session.isValid()) {
        resolve(null);
        return;
      }

      const idToken = session.getIdToken();
      const expiresAt = idToken.getExpiration(); // unix seconds
      const nowSeconds = Math.floor(Date.now() / 1000);

      if (expiresAt - nowSeconds > REFRESH_SKEW_SECONDS) {
        resolve(idToken.getJwtToken());
        return;
      }

      const refreshToken = session.getRefreshToken();
      user.refreshSession(refreshToken, (refreshError, refreshed) => {
        if (refreshError || !refreshed) {
          resolve(null);
          return;
        }
        resolve(refreshed.getIdToken().getJwtToken());
      });
    });
  });
}

/** Signs the current user out (revokes local tokens) and clears the cache. */
export function signOut(): void {
  const user = getUserPool().getCurrentUser();
  if (user) user.signOut();
}

/**
 * Changes the signed-in user's password. Requires the current valid session
 * (Cognito re-verifies `oldPassword` server-side). Rejects with a friendly
 * pt-BR Error message mapped from the Cognito error code.
 */
export function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  const user = getUserPool().getCurrentUser();
  if (!user) {
    return Promise.reject(new Error('Sessão expirada. Entre novamente.'));
  }

  return new Promise<void>((resolve, reject) => {
    user.getSession((sessionError: Error | null, session: CognitoUserSession | null) => {
      if (sessionError || !session || !session.isValid()) {
        reject(new Error('Sessão expirada. Entre novamente.'));
        return;
      }

      user.changePassword(oldPassword, newPassword, (err) => {
        if (err) {
          reject(new Error(changePasswordMessage(err)));
          return;
        }
        resolve();
      });
    });
  });
}

/** Maps Cognito change-password errors to friendly pt-BR copy. */
function changePasswordMessage(err: unknown): string {
  switch (errorKey(err)) {
    case 'NotAuthorizedException':
      return 'Senha atual incorreta.';
    case 'InvalidPasswordException':
    case 'InvalidParameterException':
      return 'A nova senha não atende à política de segurança.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Muitas tentativas. Tente novamente em alguns minutos.';
    default:
      return 'Não foi possível alterar a senha. Tente novamente.';
  }
}

/**
 * Starts self-service password recovery: Cognito e-mails a verification code to
 * the address via the pool's `verified_email` recovery mechanism. Resolves once
 * the code has been sent.
 *
 * Account non-enumeration: the SPA app client has `prevent_user_existence_errors`
 * ENABLED, so Cognito already returns success for unknown e-mails. But for some
 * account states (unconfirmed, FORCE_CHANGE_PASSWORD) Cognito still raises
 * `NotAuthorizedException` / `InvalidParameterException`, and for unknown users
 * `UserNotFoundException` — surfacing any of these would create a differential
 * that lets a caller tell a registered address apart from an unknown one. We
 * RESOLVE on all three so the UI advances identically in every case. Only real
 * operational errors (rate limiting, network) reject.
 */
export function requestPasswordReset(email: string): Promise<void> {
  const user = buildUser(email);
  return new Promise<void>((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => {
        switch (errorKey(err)) {
          case 'UserNotFoundException':
          case 'NotAuthorizedException':
          case 'InvalidParameterException':
            resolve();
            return;
          default:
            reject(new Error(requestResetMessage(err)));
        }
      },
    });
  });
}

/**
 * Completes recovery: Cognito verifies the e-mailed `code` and sets
 * `newPassword` (which must satisfy the pool password policy). Resolves on
 * success; rejects with friendly pt-BR copy otherwise.
 */
export function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  const user = buildUser(email);
  return new Promise<void>((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(new Error(confirmResetMessage(err))),
    });
  });
}

/**
 * Maps `forgotPassword` (request-code) errors to friendly pt-BR copy. Only the
 * errors that actually reject reach here: `NotAuthorizedException` /
 * `InvalidParameterException` / `UserNotFoundException` resolve for
 * non-enumeration (see `requestPasswordReset`) and never hit this mapper.
 */
function requestResetMessage(err: unknown): string {
  switch (errorKey(err)) {
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
    case 'NetworkError':
      return 'Não foi possível alcançar o servidor de autenticação. Verifique sua conexão.';
    default:
      return 'Não foi possível enviar o código. Tente novamente.';
  }
}

/** Maps `confirmPassword` (verify-code + set-password) errors to pt-BR copy. */
function confirmResetMessage(err: unknown): string {
  switch (errorKey(err)) {
    case 'CodeMismatchException':
    // Non-enumeration: mirror the generic "wrong code" copy for unknown users.
    case 'UserNotFoundException':
      return 'Código inválido. Confira o código enviado por e-mail.';
    case 'ExpiredCodeException':
      return 'O código expirou. Solicite um novo código.';
    case 'InvalidPasswordException':
    case 'InvalidParameterException':
      return `A nova senha não atende à política de segurança. ${PASSWORD_HINT}`;
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
    case 'NetworkError':
      return 'Não foi possível alcançar o servidor de autenticação. Verifique sua conexão.';
    default:
      return 'Não foi possível redefinir a senha. Tente novamente.';
  }
}

/** Extracts the Cognito error discriminator (`code` preferred, else `name`). */
function errorKey(err: unknown): string {
  if (typeof err !== 'object' || err === null) return '';
  const e = err as { code?: unknown; name?: unknown };
  if (typeof e.code === 'string' && e.code) return e.code;
  if (typeof e.name === 'string' && e.name) return e.name;
  return '';
}

/**
 * Decodes the ID token payload to read identity claims. No signature
 * verification is done here: the API verifies the token server-side. Returns
 * null on a malformed token.
 */
export function decodeIdToken(jwt: string): IdTokenClaims | null {
  const payload = jwt.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as Record<string, unknown>;

    const sub = typeof claims.sub === 'string' ? claims.sub : '';
    if (!sub) return null;

    const groupsRaw = claims['cognito:groups'];
    const groups = Array.isArray(groupsRaw)
      ? groupsRaw.filter((g): g is string => typeof g === 'string')
      : [];

    const email = typeof claims.email === 'string' ? claims.email : null;
    return { sub, email, name: nameFromClaims(claims, email, sub), groups };
  } catch {
    return null;
  }
}

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * What to call the person, by the backend's rule (`domain/display_name.py`):
 * `name`, then `given_name` + `family_name`, then the e-mail's local part.
 *
 * Never `cognito:username`: the pool signs in by e-mail, which makes it the
 * account's SUBJECT — a UUID — and reading it as the name is how the console
 * came to greet people by their UUID. Anything shaped like one, or equal to
 * the subject, is skipped at every step; null is a real answer.
 */
export function nameFromClaims(
  claims: Record<string, unknown>,
  email: string | null,
  sub: string,
): string | null {
  const presentable = (value: unknown): string | null => {
    const text = typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';
    return text && !UUID_SHAPE.test(text) && text !== sub ? text : null;
  };
  const halves = [claims.given_name, claims.family_name]
    .map(presentable)
    .filter((part): part is string => part !== null)
    .join(' ');
  const at = email?.indexOf('@') ?? -1;
  const localPart = email && at > 0 ? email.slice(0, at) : null;
  return presentable(claims.name) ?? presentable(halves) ?? presentable(localPart);
}
